/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
import { AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  StackItem,
  Tag,
  useDisclosure,
  useToast,
  VStack,
  IconButton,
  HStack,
} from '@chakra-ui/react';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Api } from 'api';
import { DataTable } from 'components';
import { FormControlFormInput, FormControlFormSelect, GenericMessages, getErrorMessage } from 'core';
import React from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  ExpertisePointGroupType,
  ExpertisePointGroupTypeTitle,
  ExpertisePointQueryCacheKey,
  ProductPriceList,
} from 'types';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { ProductSelect } from 'components/ProductSelect';
import { t, Trans } from '@lingui/macro';
import { AdminPageTitle } from '../components';

const validationSchema = yup.object({
  buyPrice: yup.number().required(GenericMessages.CannotBeEmpty),
  price: yup.number().required(GenericMessages.CannotBeEmpty),
  expertisePointGroupTypeId: yup.number().required(GenericMessages.CannotBeEmpty),
  productId: yup.number().required(GenericMessages.CannotBeEmpty),
});

export const AdminProductPricePage: React.FC = () => {
  const columnHelper = createColumnHelper<any>();
  const toast = useToast();
  const formRef = React.useRef<any>(null);

  const {
    isOpen: isAddProductPriceDialogOpen,
    onClose: onAddProductPriceDialogClose,
    onOpen: onAddProductPriceDialogOpen,
  } = useDisclosure();

  const {
    isOpen: isEditProductPriceDialogOpen,
    onClose: onEditProductPriceDialogClose,
    onOpen: onEditProductPriceDialogOpen,
  } = useDisclosure();

  const [selectedProduct, setSelectedProduct] = React.useState<ProductPriceList | null>(null);

  const { data: allProductPriceList, refetch: refetchAllProductPrices } = useQuery(
    [ExpertisePointQueryCacheKey.GetProductPriceList],
    async () => {
      const response = await Api.ExpertisePoint.getProductPriceList();

      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      cacheTime: 0,
      onError(error: any) {
        toast({
          isClosable: true,
          status: 'error',
          title: getErrorMessage(error, 'Ürün fiyat listesini çekerken hata oluştu'),
        });
      },
    },
  );

  const saveProductPriceMutation = useMutation<any, any, ProductPriceList>(
    async (productPrice: ProductPriceList) => {
      const response = await Api.ExpertisePoint.saveProductPrice(productPrice);
      return response.data;
    },
    {
      onSuccess: () => {
        toast({
          isClosable: true,
          status: 'success',
          title: 'Ürün fiyatı kaydedildi',
        });
        onAddProductPriceDialogClose();
        onEditProductPriceDialogClose();
        refetchAllProductPrices();
        if (formRef.current) {
          formRef.current.resetForm();
        }
      },
      onError: () => {
        toast({
          isClosable: true,
          status: 'error',
          title: 'Ürün fiyatı kaydedilirken hata oluştu',
        });
      },
    },
  );

  const formik = useFormik({
    initialValues: {
      buyPrice: 0,
      price: 0,
      expertisePointGroupTypeId: (() => {
        const saved = localStorage.getItem('selectedExpertisePointGroupType');
        return saved ? parseInt(saved, 10) : ExpertisePointGroupType.OccoExpertise;
      })(),
      productId: 0,
    },
    onSubmit: (formData) => {
      saveProductPriceMutation.mutateAsync({
        buyPrice: formData.buyPrice,
        price: formData.price,
        expertisePointGroupTypeId: formData.expertisePointGroupTypeId,
        productId: formData.productId,
      });
    },
    validationSchema,
  });

  formRef.current = formik;

  const { setFieldValue, values, handleChange, errors, handleSubmit } = formik;

  const deleteProductPriceMutation = useMutation<any, any, number>(
    async (productId: number) => {
      const response = await Api.ExpertisePoint.deleteProductPrice(productId);
      return response.data;
    },
    {
      onSuccess: () => {
        toast({
          isClosable: true,
          status: 'success',
          title: 'Ürün fiyatı silindi',
        });
        refetchAllProductPrices();
      },
      onError: () => {
        toast({
          isClosable: true,
          status: 'error',
          title: 'Ürün fiyatı silinirken hata oluştu',
        });
      },
    },
  );

  const handleEdit = (product: ProductPriceList) => {
    setSelectedProduct(product);
    setFieldValue('buyPrice', product.buyPrice);
    setFieldValue('price', product.price);
    setFieldValue('expertisePointGroupTypeId', product.expertisePointGroupTypeId);
    setFieldValue('productId', product.productId);
    onEditProductPriceDialogOpen();
  };

  const handleDelete = async (productId: number) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Bu ürün fiyatını silmek istediğinize emin misiniz?')) {
      await deleteProductPriceMutation.mutateAsync(productId);
    }
  };

  const columns: ColumnDef<any, string>[] = [
    columnHelper.accessor('expertisePointGroupTypeId', {
      header: t`Ürün Grubu`,
      cell: (info) => {
        const { original } = info.row;
        return (
          <Tag colorScheme={original.expertisePointGroupTypeId % 2 === 0 ? 'red' : 'green'}>
            {ExpertisePointGroupTypeTitle[original.expertisePointGroupTypeId]}
          </Tag>
        );
      },
    }),
    columnHelper.accessor('title', {
      header: t`Ürün Adı`,
      cell: (info) => {
        const { original } = info.row;
        return original.product.name;
      },
    }),
    columnHelper.accessor('price', {
      header: t`Satış Fiyatı`,
      cell: (info) => {
        const { original } = info.row;
        return original.price;
      },
    }),
    columnHelper.accessor('buyPrice', {
      header: t`Alış Fiyatı`,
      cell: (info) => {
        const { original } = info.row;
        return original.buyPrice;
      },
    }),
    columnHelper.accessor('actions', {
      header: 'İşlemler',
      cell: (info) => {
        const { original } = info.row;
        return (
          <HStack spacing={2}>
            <IconButton
              aria-label="Düzenle"
              icon={<EditIcon />}
              size="sm"
              colorScheme="blue"
              onClick={() => handleEdit(original)}
            />
            <IconButton
              aria-label="Sil"
              icon={<DeleteIcon />}
              size="sm"
              colorScheme="red"
              onClick={() => handleDelete(original.productId)}
            />
          </HStack>
        );
      },
    }),
  ];

  const actionButtons = (
    <Button leftIcon={<AddIcon />} aria-label={t`Ekle`} onClick={onAddProductPriceDialogOpen}>
      <Trans>Ekle</Trans>
    </Button>
  );

  return (
    <>
      <AdminPageTitle title={t`Ürün Fiyat Listesi`} />
      <DataTable
        columns={columns}
        data={allProductPriceList || []}
        tableProps={{
          variant: 'striped',
          colorScheme: 'teal',
        }}
        title={t`Ürün Fiyat Listesi`}
        actions={actionButtons}
        usePagination
      />
      <Modal isOpen={isAddProductPriceDialogOpen} onClose={onAddProductPriceDialogClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Ürün Fiyatı Ekle</Trans>
          </ModalHeader>
          <form id="createProductPriceDialog" noValidate onSubmit={handleSubmit}>
            <ModalBody>
              <VStack align="stretch" gap={2}>
                <StackItem>
                  <ProductSelect
                    value={values.productId}
                    onChange={(e) => {
                      setFieldValue('productId', parseInt(e.target.value, 10));
                    }}
                    error={errors.productId != null}
                    helperText={errors.productId}
                  />
                </StackItem>
                <StackItem>
                  <FormControlFormSelect
                    mb="10"
                    name="expertisePointGroupTypeId"
                    value={values.expertisePointGroupTypeId}
                    onChange={(e) => {
                      setFieldValue('expertisePointGroupTypeId', parseInt(e.target.value, 10));
                    }}
                  >
                    <option value={ExpertisePointGroupType.OccoExpertise}>
                      <Trans>Occo Ekspertiz</Trans>
                    </option>
                    <option value={ExpertisePointGroupType.AutoKaleExpertise}>
                      <Trans>Auto Kale Ekspertiz</Trans>
                    </option>
                  </FormControlFormSelect>
                </StackItem>
                <StackItem>
                  <FormControlFormInput
                    label={t`Satış Fiyatı`}
                    placeholder={t`Satış Fiyatı`}
                    value={values.price}
                    onChange={handleChange}
                    name="price"
                    error={errors.price != null}
                    helperText={errors.price}
                    type="number"
                  />
                </StackItem>
                <StackItem>
                  <FormControlFormInput
                    label={t`Alış Fiyatı`}
                    placeholder={t`Alış Fiyatı`}
                    value={values.buyPrice}
                    onChange={handleChange}
                    name="buyPrice"
                    error={errors.buyPrice != null}
                    helperText={errors.buyPrice}
                    type="number"
                  />
                </StackItem>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button mr={3} onClick={onAddProductPriceDialogClose}>
                İptal
              </Button>
              <Button colorScheme="blue" type="submit" isLoading={saveProductPriceMutation.isLoading}>
                Kaydet
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      <Modal isOpen={isEditProductPriceDialogOpen} onClose={onEditProductPriceDialogClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Ürün Fiyatı Düzenle</ModalHeader>
          <form
            id="editProductPriceDialog"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(e);
              onEditProductPriceDialogClose();
            }}
          >
            <ModalBody>
              <VStack align="stretch" gap={2}>
                <StackItem>
                  <ProductSelect
                    value={values.productId}
                    onChange={(e) => {
                      setFieldValue('productId', parseInt(e.target.value, 10));
                    }}
                    error={errors.productId != null}
                    helperText={errors.productId}
                    isDisabled
                  />
                </StackItem>
                <StackItem>
                  <FormControlFormSelect
                    mb="10"
                    name="expertisePointGroupTypeId"
                    value={values.expertisePointGroupTypeId}
                    onChange={(e) => {
                      setFieldValue('expertisePointGroupTypeId', parseInt(e.target.value, 10));
                    }}
                    isDisabled
                  >
                    <option value={ExpertisePointGroupType.OccoExpertise}>Occo Ekspertiz</option>
                    <option value={ExpertisePointGroupType.AutoKaleExpertise}>Auto Kale Ekspertiz</option>
                  </FormControlFormSelect>
                </StackItem>
                <StackItem>
                  <FormControlFormInput
                    label="Satış Fiyatı"
                    placeholder="Satış Fiyatı"
                    value={values.price}
                    onChange={handleChange}
                    name="price"
                    error={errors.price != null}
                    helperText={errors.price}
                    type="number"
                  />
                </StackItem>
                <StackItem>
                  <FormControlFormInput
                    label="Alış Fiyatı"
                    placeholder="Alış Fiyatı"
                    value={values.buyPrice}
                    onChange={handleChange}
                    name="buyPrice"
                    error={errors.buyPrice != null}
                    helperText={errors.buyPrice}
                    type="number"
                  />
                </StackItem>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button mr={3} onClick={onEditProductPriceDialogClose}>
                <Trans>İptal</Trans>
              </Button>
              <Button colorScheme="blue" type="submit" isLoading={saveProductPriceMutation.isLoading}>
                <Trans>Güncelle</Trans>
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
};
